import type { IconName, TagVariants } from '@cencosud-cencommerce/paris-uikit';

import type { CartDiscount } from '@/modules/cart/domain/CartDiscount';
import type { CartLineItem } from '@/modules/cart/domain/CartLineItem';
import type { CartOutOfStockItem } from '@/modules/cart/domain/CartOutOfStockItem';
import type {
  CartDiscountedPricePerQuantity,
  CartProductOption
} from '@/modules/cart/domain/CartProductOption';
import type { HttpCartDiscountDto } from '@/modules/cart/infrastructure/dto/HttpCartDiscountDto';
import type {
  CartDiscountedPricePerQuantityDto,
  HttpCartProductOptionDto
} from '@/modules/cart/infrastructure/dto/HttpCartProductOptionDto';
import type { HttpOutOfStockItemDto } from '@/modules/cart/infrastructure/dto/HttpOutOfStockItemDto';
import {
  getDeliveryGroupsOptionsBySku,
  variantDtoToVariant
} from '@/modules/product/infrastructure/mappers/httpProductDetailsDtoToProductDetails';

import type { Cart } from '../../domain/Cart';
import environment from '../../environments/environment';

import type {
  Attribute,
  HttpCartDto,
  LineItem,
  LocaleString
} from './../dto/HttpCartRepositoryDto';

const { LOCALE } = environment;
const currentLocale = LOCALE as keyof LocaleString;

const mapVariantOptions = (
  colorAttr: Attribute | undefined,
  sizeAttr: Attribute | undefined
): CartLineItem['variantOptions'] => {
  const labels = [];
  const values = [];

  if (colorAttr) {
    const colorValue = colorAttr.value as string;
    labels.push(colorValue);
    values.push(colorValue.toLowerCase());
  }

  if (sizeAttr) {
    const sizeValue = sizeAttr.value as string;
    labels.push(sizeAttr.value);
    values.push(sizeValue.toLowerCase());
  }

  const label = labels.join(', ');
  const value = values.join('-');

  return { label, value };
};

const mapSelectedOptions = (
  colorAttr: Attribute | undefined,
  sizeAttr: Attribute | undefined
) => {
  const selectedOptions = [];

  if (colorAttr) {
    selectedOptions.push({
      label: 'Color',
      value: colorAttr.value as string
    });
  }

  if (sizeAttr) {
    selectedOptions.push({
      label: 'Talla',
      value: sizeAttr.value as string
    });
  }

  return selectedOptions;
};

export const lineItemDtotoCartLineItem = (
  lineItemDto: LineItem,
  cartData: HttpCartDto
): CartLineItem => {
  const { deliveryInfo } = cartData;
  const colorAttribute = lineItemDto?.variant?.attributes?.find(
    (attribute) =>
      attribute.name === 'color' && attribute.value !== 'Color Unico'
  );
  const sizeAttribute = lineItemDto?.variant?.attributes?.find(
    (attribute) =>
      attribute.name === 'size' && attribute.value !== 'Talla Unica'
  );

  const variantOptions = mapVariantOptions(colorAttribute, sizeAttribute);
  const selectedOptions = mapSelectedOptions(colorAttribute, sizeAttribute);
  const name = lineItemDto?.name['es-CL'] ?? '';

  const deliveryGroups = getDeliveryGroupsOptionsBySku(
    deliveryInfo?.deliveryGroups ?? [],
    lineItemDto?.variant.sku ?? ''
  );

  return {
    id: lineItemDto?.id ?? '',
    productKey: lineItemDto?.productKey ?? '',
    productId: lineItemDto?.productId ?? '',
    name,
    productType: {
      key: lineItemDto?.productType?.key
    },
    quantity: lineItemDto?.quantity ?? 0,
    variant: variantDtoToVariant(lineItemDto.variant, name),
    price: lineItemDto?.price,
    variantOptions,
    selectedOptions,
    sampleAvailable: false,
    availableShippingOptions: [
      { label: 'Recibe hoy', icon: 'flash' as IconName },
      { label: 'Despacho 24hs', icon: 'dt-delivery-express' as IconName },
      { label: 'Retiro', icon: 'dt-shop' as IconName }
    ],
    discountedPricePerQuantity: lineItemDto?.discountedPricePerQuantity,
    seller: lineItemDto.variant.sellers?.length
      ? lineItemDto.variant.sellers[0]
      : undefined,
    aditionalServices: [],
    productTags: [
      { variant: 'employee' as TagVariants, label: 'Descuento Colaborador' },
      { variant: 'information' as TagVariants, label: 'Recibe gratis hoy' }
    ],
    deliveryGroups: deliveryGroups,
    custom: lineItemDto?.custom
  };
};

const cartDiscountMapper = (
  cartDiscountDTO: HttpCartDiscountDto
): CartDiscount => ({
  ...cartDiscountDTO,
  name: cartDiscountDTO.name[currentLocale],
  description: cartDiscountDTO.description[currentLocale]
});

const cartDiscountedPricePerQuantityMapper = (
  discountedPrice: CartDiscountedPricePerQuantityDto
): CartDiscountedPricePerQuantity => ({
  ...discountedPrice,
  discountedPrice: {
    value: discountedPrice.discountedPrice.value,
    includedDiscounts: discountedPrice.discountedPrice.includedDiscounts.map(
      (includeDiscount) => ({
        discount: cartDiscountMapper(includeDiscount.discount.obj),
        discountedAmount: includeDiscount.discountedAmount
      })
    )
  }
});

const cartProductOptionMapper = (
  option: HttpCartProductOptionDto
): CartProductOption => ({
  ...option,
  name: option.name[currentLocale],
  discountedPricePerQuantity: option.discountedPricePerQuantity.map(
    cartDiscountedPricePerQuantityMapper
  )
});

const cartOutOfStockItemsMapper = (
  outOfStockItemsDto: HttpOutOfStockItemDto
): CartOutOfStockItem => ({
  ...outOfStockItemsDto,
  storeName: outOfStockItemsDto.sellers?.[0],
  name: outOfStockItemsDto.name['es-CL']
});

export const httpCartDtoToCart = (httpCart: HttpCartDto): Cart => {
  const lineItems = httpCart.lineItems.map((lineItem) =>
    lineItemDtotoCartLineItem(lineItem, httpCart)
  );

  const productOptions = httpCart.productOptions.map(cartProductOptionMapper);

  const outOfStockItems = httpCart?.outOfStockItems?.length
    ? httpCart.outOfStockItems.map(cartOutOfStockItemsMapper)
    : undefined;

  const shippingAddress = httpCart.shippingAddress;

  return {
    ...httpCart,
    shippingAddress: shippingAddress
      ? {
          ...shippingAddress,
          region: Number(shippingAddress.region ?? 0)
        }
      : undefined,
    lineItems,
    productOptions,
    outOfStockItems
  };
};
